/* =============================================================================
   Solva — behaviour
   Custom elements only. No globals, no dependencies, safe to load twice.

   Event contract
     solva:variant:change  product form -> everyone  (variant, qty, total, media)
     solva:sync            late listener -> product form ("re-broadcast, please")
     solva:add             any CTA -> product form   ("submit the real form")
     solva:busy            product form -> everyone  (submit in flight)
     solva:cart:change     product form -> everyone  (fresh /cart.js payload)
   ============================================================================= */
(function () {
  'use strict';

  var define = function (name, ctor) {
    if (!window.customElements.get(name)) window.customElements.define(name, ctor);
  };

  var on = function (el, type, fn, opts) {
    el.addEventListener(type, fn, opts);
  };

  var root = function () {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  };

  var BUS = 'solva:variant:change';
  var SYNC = 'solva:sync';
  var CART = 'solva:cart:change';

  /* ---------------------------------------------------------------------------
     <solva-gallery>
     Stage: one media per view, scroll-snap.
     Thumbnails: a single-row slider that pages by a whole view at a time.

     Note on scrolling: the thumbnail track is moved by assigning scrollLeft
     rather than calling scrollBy/scrollTo with behavior:'smooth'. On a
     scroll-snap container Chrome silently drops animated programmatic scrolls,
     so the smooth variants simply do nothing. CSS snapping supplies the settle.
     --------------------------------------------------------------------------- */
  class SolvaGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-stage]');
      this.thumbTrack = this.querySelector('[data-thumbs]');
      this.prevBtn = this.querySelector('[data-thumb-prev]');
      this.nextBtn = this.querySelector('[data-thumb-next]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-thumb]'));
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
      if (!this.stage) return;

      this.thumbs.forEach(
        function (thumb) {
          on(thumb, 'click', function () {
            this.goTo(thumb.dataset.thumb);
          }.bind(this));
          on(thumb, 'keydown', this.onThumbKey.bind(this));
        }.bind(this)
      );

      if (this.prevBtn) on(this.prevBtn, 'click', this.pageThumbs.bind(this, -1));
      if (this.nextBtn) on(this.nextBtn, 'click', this.pageThumbs.bind(this, 1));
      if (this.thumbTrack) {
        this.onTrackScroll = this.updateArrows.bind(this);
        on(this.thumbTrack, 'scroll', this.onTrackScroll, { passive: true });
      }
      this.onResize = this.updateArrows.bind(this);
      on(window, 'resize', this.onResize, { passive: true });
      this.updateArrows();

      // Keep the active thumbnail in sync with swipe / scroll position on the stage
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(
              function (entry) {
                if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                  this.setActive(entry.target.dataset.slide);
                }
              }.bind(this)
            );
          }.bind(this),
          { root: this.stage, threshold: [0.61] }
        );
        this.slides.forEach(function (s) { this.observer.observe(s); }.bind(this));
      }

      // Lightbox
      this.dialog = this.querySelector('[data-lightbox]');
      if (this.dialog) {
        this.lightboxImg = this.dialog.querySelector('img');
        this.slides.forEach(
          function (slide) {
            var trigger = slide.querySelector('[data-zoom]');
            if (!trigger) return;
            on(trigger, 'click', function () {
              this.openLightbox(trigger.dataset.zoom, trigger.dataset.zoomAlt || '');
            }.bind(this));
          }.bind(this)
        );
        var close = this.dialog.querySelector('[data-lightbox-close]');
        if (close) on(close, 'click', function () { this.dialog.close(); }.bind(this));
        on(this.dialog, 'click', function (e) {
          if (e.target === this.dialog) this.dialog.close();
        }.bind(this));
      }

      // Variant -> media
      this.onVariant = function (e) {
        if (e.detail.sectionId !== this.dataset.sectionId) return;
        if (e.detail.mediaId) this.goToMedia(e.detail.mediaId);
      }.bind(this);
      on(document, BUS, this.onVariant);

      // The active thumbnail is decided server-side; bring it into view once laid out.
      requestAnimationFrame(
        function () {
          this.revealThumb(this.querySelector('[data-thumb][aria-current="true"]'));
        }.bind(this)
      );
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener(BUS, this.onVariant);
      window.removeEventListener('resize', this.onResize);
    }

    onThumbKey(e) {
      var idx = this.thumbs.indexOf(e.currentTarget);
      var next = null;
      if (e.key === 'ArrowRight') next = this.thumbs[idx + 1];
      if (e.key === 'ArrowLeft') next = this.thumbs[idx - 1];
      if (!next) return;
      e.preventDefault();
      next.focus();
      this.goTo(next.dataset.thumb);
    }

    /* Width of one thumbnail page: thumb width plus gap, times thumbs per view. */
    thumbPageWidth() {
      var track = this.thumbTrack;
      var first = this.thumbs[0];
      if (!track || !first) return 0;
      var styles = getComputedStyle(track);
      var gap = parseFloat(styles.columnGap) || 8;
      var perView = parseInt(styles.getPropertyValue('--sv-thumb-per-view'), 10) || 4;
      return (first.getBoundingClientRect().width + gap) * perView;
    }

    pageThumbs(direction) {
      var track = this.thumbTrack;
      if (!track) return;
      var page = this.thumbPageWidth() || track.clientWidth;
      var max = track.scrollWidth - track.clientWidth;
      track.scrollLeft = Math.max(0, Math.min(max, track.scrollLeft + direction * page));
      this.updateArrows();
    }

    updateArrows() {
      var track = this.thumbTrack;
      if (!track || (!this.prevBtn && !this.nextBtn)) return;
      var max = track.scrollWidth - track.clientWidth;
      var fits = max <= 1;
      if (this.prevBtn) {
        this.prevBtn.hidden = fits;
        this.prevBtn.disabled = track.scrollLeft <= 1;
      }
      if (this.nextBtn) {
        this.nextBtn.hidden = fits;
        this.nextBtn.disabled = track.scrollLeft >= max - 1;
      }
    }

    /* Nudge the track only as far as needed to reveal a thumbnail. */
    revealThumb(thumb) {
      var track = this.thumbTrack;
      if (!track || !thumb) return;
      var t = track.getBoundingClientRect();
      var b = thumb.getBoundingClientRect();
      var delta = 0;
      if (b.left < t.left) delta = b.left - t.left;
      else if (b.right > t.right) delta = b.right - t.right;
      if (!delta) return;
      var max = track.scrollWidth - track.clientWidth;
      track.scrollLeft = Math.max(0, Math.min(max, track.scrollLeft + delta));
      this.updateArrows();
    }

    goTo(index) {
      var slide = this.slides.filter(function (s) { return s.dataset.slide === String(index); })[0];
      if (!slide) return;
      this.stage.scrollTo({ left: slide.offsetLeft - this.stage.offsetLeft, behavior: 'smooth' });
      this.setActive(index);
    }

    goToMedia(mediaId) {
      var slide = this.slides.filter(function (s) { return s.dataset.mediaId === String(mediaId); })[0];
      if (slide) this.goTo(slide.dataset.slide);
    }

    setActive(index) {
      var active = null;
      this.thumbs.forEach(function (t) {
        var isActive = t.dataset.thumb === String(index);
        t.setAttribute('aria-current', isActive ? 'true' : 'false');
        if (isActive) active = t;
      });
      this.revealThumb(active);
    }

    openLightbox(src, alt) {
      if (!this.dialog || !this.lightboxImg) return;
      this.lightboxImg.src = src;
      this.lightboxImg.alt = alt;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    }
  }
  define('solva-gallery', SolvaGallery);

  /* ---------------------------------------------------------------------------
     <solva-product-form> — variant matching, price, quantity, bundles, ATC
     --------------------------------------------------------------------------- */
  class SolvaProductForm extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('form');
      this.idInput = this.querySelector('[data-variant-id]');
      this.qtyInput = this.querySelector('[data-quantity-input]');
      this.submit = this.querySelector('[data-atc]');
      this.toast = this.querySelector('[data-toast]');
      this.behaviour = this.dataset.behaviour || 'toast';

      var data = this.querySelector('[data-variant-data]');
      try {
        this.variants = data ? JSON.parse(data.textContent) : [];
      } catch (err) {
        this.variants = [];
      }

      this.optionInputs = Array.prototype.slice.call(this.querySelectorAll('[data-option-input]'));
      this.tierInputs = Array.prototype.slice.call(this.querySelectorAll('[data-tier-input]'));

      this.optionInputs.forEach(function (input) {
        on(input, 'change', this.render.bind(this));
      }.bind(this));

      this.tierInputs.forEach(function (input) {
        on(input, 'change', this.onTierChange.bind(this));
      }.bind(this));

      var stepper = this.querySelector('[data-quantity]');
      if (stepper && this.qtyInput) {
        Array.prototype.slice.call(stepper.querySelectorAll('[data-step]')).forEach(
          function (btn) {
            on(btn, 'click', function () {
              var delta = parseInt(btn.dataset.step, 10);
              var min = parseInt(this.qtyInput.min || '1', 10);
              this.qtyInput.value = Math.max(min, (parseInt(this.qtyInput.value, 10) || min) + delta);
              this.render();
            }.bind(this));
          }.bind(this)
        );
        on(this.qtyInput, 'change', this.render.bind(this));
      }

      if (this.form) on(this.form, 'submit', this.onSubmit.bind(this));

      this.onExternalAdd = function (e) {
        if (e.detail && e.detail.sectionId && e.detail.sectionId !== this.sectionId) return;
        if (!this.form) return;
        if (this.form.requestSubmit) this.form.requestSubmit();
        else this.form.submit();
      }.bind(this);
      on(document, 'solva:add', this.onExternalAdd);

      // Elements further down the page (sticky bar, secondary CTAs) upgrade after
      // this one, so they miss the first broadcast. They ask for it with solva:sync.
      this.onSync = function () { this.render(); }.bind(this);
      on(document, SYNC, this.onSync);

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('solva:add', this.onExternalAdd);
      document.removeEventListener(SYNC, this.onSync);
    }

    get quantity() {
      var tier = this.selectedTier();
      if (tier) return parseInt(tier.dataset.tierQty, 10) || 1;
      return Math.max(1, parseInt(this.qtyInput && this.qtyInput.value, 10) || 1);
    }

    selectedTier() {
      return this.tierInputs.filter(function (i) { return i.checked; })[0] || null;
    }

    selectedOptions() {
      var byPosition = {};
      this.optionInputs.forEach(function (input) {
        if (input.checked) byPosition[input.dataset.optionPosition] = input.value;
      });
      return byPosition;
    }

    matchVariant() {
      var selected = this.selectedOptions();
      var keys = Object.keys(selected);
      if (!keys.length) return this.variants[0] || null;
      return (
        this.variants.filter(function (variant) {
          return keys.every(function (pos) {
            return variant.options[parseInt(pos, 10) - 1] === selected[pos];
          });
        })[0] || null
      );
    }

    onTierChange() {
      if (this.qtyInput) this.qtyInput.value = this.quantity;
      this.render();
    }

    render() {
      var variant = this.matchVariant();
      this.current = variant;

      if (this.idInput) this.idInput.value = variant ? variant.id : '';
      if (this.qtyInput) this.qtyInput.value = this.quantity;

      var selected = this.selectedOptions();

      // Mark option values that cannot be bought in the current combination
      this.optionInputs.forEach(function (input) {
        var wrapper = input.closest('[data-option-wrapper]');
        if (!wrapper) return;
        var available = this.optionValueAvailable(input.dataset.optionPosition, input.value, selected);
        if (wrapper.classList.contains('sv-swatch')) {
          wrapper.classList.toggle('sv-swatch--soldout', !available);
        } else {
          wrapper.classList.toggle('sv-pill--soldout', !available);
        }
      }.bind(this));

      Array.prototype.slice.call(this.querySelectorAll('[data-option-value-for]')).forEach(
        function (node) {
          var value = selected[node.dataset.optionValueFor];
          if (value) node.textContent = value;
        }
      );

      var qty = this.quantity;
      var priceData = this.priceFor(variant, qty);

      this.setText('[data-price-now]', priceData.total);
      this.setText('[data-price-was]', priceData.compare);
      this.setNode('[data-price-was]', priceData.compare !== null);
      this.setText('[data-price-save]', priceData.saveLabel);
      this.setNode('[data-price-save]', !!priceData.saveLabel);
      this.setText('[data-price-unit]', priceData.unitLabel);
      this.setNode('[data-price-unit]', !!priceData.unitLabel);

      this.tierInputs.forEach(function (input) {
        var tierQty = parseInt(input.dataset.tierQty, 10) || 1;
        var tierPrice = this.priceFor(variant, tierQty, parseFloat(input.dataset.tierDiscount || '0'));
        var card = input.parentNode;
        var totalNode = card.querySelector('[data-tier-total]');
        var compareNode = card.querySelector('[data-tier-compare]');
        if (totalNode) totalNode.textContent = tierPrice.total;
        if (compareNode) {
          compareNode.textContent = tierPrice.compare || '';
          compareNode.hidden = !tierPrice.compare;
        }
      }.bind(this));

      var available = !!(variant && variant.available);
      if (this.submit) {
        this.submit.disabled = !available;
        var label = this.submit.querySelector('[data-atc-label]') || this.submit;
        if (!variant) label.textContent = this.dataset.unavailableText || 'Unavailable';
        else if (!available) label.textContent = this.dataset.soldOutText || 'Sold out';
        else {
          label.textContent =
            (this.dataset.addText || 'Add to cart') +
            (this.dataset.showTotal === 'true' ? ' — ' + priceData.total : '');
        }
      }

      var stock = this.querySelector('[data-stock]');
      if (stock) {
        stock.hidden = !variant || variant.available;
        stock.textContent = variant && !variant.available ? this.dataset.soldOutText || 'Sold out' : '';
      }

      var labels = Object.keys(selected).map(function (k) { return selected[k]; });

      document.dispatchEvent(
        new CustomEvent(BUS, {
          detail: {
            sectionId: this.sectionId,
            variant: variant,
            quantity: qty,
            total: priceData.total,
            available: available,
            mediaId: variant ? variant.media_id : null,
            optionsLabel: labels.join(' · ')
          }
        })
      );

      // Keep the URL honest for sharing and analytics
      if (variant && window.history && window.history.replaceState && !(window.Shopify && window.Shopify.designMode)) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    optionValueAvailable(position, value, selected) {
      var idx = parseInt(position, 10) - 1;
      return this.variants.some(function (variant) {
        if (variant.options[idx] !== value) return false;
        if (!variant.available) return false;
        return Object.keys(selected).every(function (pos) {
          var i = parseInt(pos, 10) - 1;
          if (i === idx) return true;
          return variant.options[i] === selected[pos];
        });
      });
    }

    /* Money is formatted with the shop's own money format so currency and
       separators stay correct in every market. */
    priceFor(variant, qty, discountPercent) {
      if (!variant) return { total: '—', compare: null, saveLabel: '', unitLabel: '' };
      var pct = discountPercent || 0;
      var totalCents = Math.round(variant.price * qty * (1 - pct / 100));
      var compareCents = variant.compare_at_price ? variant.compare_at_price * qty : 0;
      var saveCents = compareCents > totalCents ? compareCents - totalCents : 0;

      return {
        total: this.money(totalCents),
        compare: compareCents > totalCents ? this.money(compareCents) : null,
        saveLabel: saveCents
          ? (this.dataset.saveText || 'You save {{ amount }}').replace(/\{\{\s*amount\s*\}\}/, this.money(saveCents))
          : '',
        unitLabel: qty > 1 ? this.money(Math.round(totalCents / qty)) + ' ' + (this.dataset.eachText || 'each') : ''
      };
    }

    money(cents) {
      var format = this.dataset.moneyFormat || '${{amount}}';
      var parts = (cents / 100).toFixed(2).split('.');
      var comma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var dot = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return format
        .replace(/\{\{\s*amount\s*\}\}/g, comma + '.' + parts[1])
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, comma)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dot + ',' + parts[1])
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, dot);
    }

    setText(selector, text) {
      Array.prototype.slice.call(this.querySelectorAll(selector)).forEach(function (node) {
        node.textContent = text === null ? '' : text;
      });
    }

    setNode(selector, visible) {
      Array.prototype.slice.call(this.querySelectorAll(selector)).forEach(function (node) {
        node.hidden = !visible;
      });
    }

    onSubmit(event) {
      if (this.behaviour === 'page') return; // native POST to /cart/add
      event.preventDefault();
      if (!this.current || !this.current.available) return;

      var body = new FormData(this.form);
      this.setBusy(true);
      this.showToast('', false);

      fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: body
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data.description || data.message || 'Add to cart failed');
            return data;
          });
        })
        .then(function () {
          return fetch(root() + 'cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          document.dispatchEvent(new CustomEvent(CART, { detail: cart }));
          // Best-effort hooks for themes that own a cart drawer.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(
            new CustomEvent('cart:lines-update', { bubbles: true, detail: { action: 'add', cart: cart } })
          );

          if (this.behaviour === 'cart') {
            window.location.href = root() + 'cart';
            return;
          }
          if (this.behaviour === 'checkout') {
            window.location.href = '/checkout';
            return;
          }
          this.showToast(this.dataset.addedText || 'Added to your cart.', true);
        }.bind(this))
        .catch(function (err) {
          this.showToast(err.message || 'Something went wrong.', true, true);
        }.bind(this))
        .then(function () {
          this.setBusy(false);
        }.bind(this));
    }

    setBusy(busy) {
      if (this.submit) {
        this.submit.setAttribute('aria-busy', busy ? 'true' : 'false');
        this.submit.disabled = busy || !(this.current && this.current.available);
      }
      document.dispatchEvent(
        new CustomEvent('solva:busy', { detail: { sectionId: this.sectionId, busy: busy } })
      );
    }

    showToast(message, visible, isError) {
      if (!this.toast) return;
      this.toast.hidden = !visible;
      this.toast.classList.toggle('sv-toast--error', !!isError);
      this.toast.setAttribute('role', 'status');
      var text = this.toast.querySelector('[data-toast-text]');
      if (text) text.textContent = message;
    }
  }
  define('solva-product-form', SolvaProductForm);

  /* ---------------------------------------------------------------------------
     <solva-sticky-atc> — mirrors the live variant, submits the real form
     --------------------------------------------------------------------------- */
  class SolvaStickyAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sticky-atc]');
      this.priceNode = this.querySelector('[data-sticky-price]');
      this.metaNode = this.querySelector('[data-sticky-meta]');

      document.body.classList.add('sv-has-sticky');

      this.onVariant = function (e) {
        if (this.priceNode) this.priceNode.textContent = e.detail.total;
        if (this.metaNode) {
          var qty = e.detail.quantity > 1 ? '× ' + e.detail.quantity : '';
          this.metaNode.textContent = [e.detail.optionsLabel, qty].filter(Boolean).join(' · ');
        }
        if (this.button) this.button.disabled = !e.detail.available;
      }.bind(this);
      on(document, BUS, this.onVariant);

      this.onBusy = function (e) {
        if (this.button) this.button.setAttribute('aria-busy', e.detail.busy ? 'true' : 'false');
      }.bind(this);
      on(document, 'solva:busy', this.onBusy);

      // Fallback for browsers that ignore the form attribute on a button
      if (this.button && !document.getElementById(this.button.getAttribute('form') || '')) {
        on(this.button, 'click', function (e) {
          e.preventDefault();
          document.dispatchEvent(new CustomEvent('solva:add', { detail: {} }));
        });
      }

      // Reveal once the main buy box has scrolled away
      var anchor = this.dataset.anchor ? document.getElementById(this.dataset.anchor) : null;
      if (anchor && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          function (entries) {
            this.setAttribute('data-visible', entries[0].isIntersecting ? 'false' : 'true');
          }.bind(this),
          { rootMargin: '0px 0px -60% 0px' }
        );
        this.io.observe(anchor);
      } else {
        this.setAttribute('data-visible', 'true');
      }

      // This element upgrades after the product form has already broadcast, so
      // ask for a fresh one rather than showing stale server-rendered values.
      document.dispatchEvent(new CustomEvent(SYNC));
    }

    disconnectedCallback() {
      document.removeEventListener(BUS, this.onVariant);
      document.removeEventListener('solva:busy', this.onBusy);
      if (this.io) this.io.disconnect();
      document.body.classList.remove('sv-has-sticky');
    }
  }
  define('solva-sticky-atc', SolvaStickyAtc);

  /* ---------------------------------------------------------------------------
     <solva-accordion> — accessible FAQ
     --------------------------------------------------------------------------- */
  class SolvaAccordion extends HTMLElement {
    connectedCallback() {
      this.single = this.dataset.single === 'true';
      this.triggers = Array.prototype.slice.call(this.querySelectorAll('[data-accordion-trigger]'));
      this.triggers.forEach(function (trigger) {
        on(trigger, 'click', this.toggle.bind(this, trigger));
        on(trigger, 'keydown', this.onKey.bind(this));
      }.bind(this));
    }

    onKey(e) {
      var idx = this.triggers.indexOf(e.currentTarget);
      var next = null;
      if (e.key === 'ArrowDown') next = this.triggers[idx + 1] || this.triggers[0];
      if (e.key === 'ArrowUp') next = this.triggers[idx - 1] || this.triggers[this.triggers.length - 1];
      if (e.key === 'Home') next = this.triggers[0];
      if (e.key === 'End') next = this.triggers[this.triggers.length - 1];
      if (!next) return;
      e.preventDefault();
      next.focus();
    }

    toggle(trigger) {
      var expanded = trigger.getAttribute('aria-expanded') === 'true';
      if (this.single && !expanded) {
        this.triggers.forEach(function (other) {
          if (other !== trigger) this.setState(other, false);
        }.bind(this));
      }
      this.setState(trigger, !expanded);
    }

    setState(trigger, open) {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (panel) panel.hidden = !open;
      var sign = trigger.querySelector('[data-accordion-sign]');
      if (sign) sign.textContent = open ? '−' : '+';
    }
  }
  define('solva-accordion', SolvaAccordion);

  /* ---------------------------------------------------------------------------
     <solva-cart-count> — badge for the optional Solva header
     --------------------------------------------------------------------------- */
  class SolvaCartCount extends HTMLElement {
    connectedCallback() {
      this.onCart = function (e) {
        if (e.detail && typeof e.detail.item_count === 'number') this.textContent = e.detail.item_count;
      }.bind(this);
      on(document, CART, this.onCart);
    }
    disconnectedCallback() {
      document.removeEventListener(CART, this.onCart);
    }
  }
  define('solva-cart-count', SolvaCartCount);

  /* ---------------------------------------------------------------------------
     <solva-disclosure> — simple show/hide toggle
     --------------------------------------------------------------------------- */
  class SolvaDisclosure extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('[data-disclosure-trigger]');
      this.panel = this.querySelector('[data-disclosure-panel]');
      if (!this.trigger || !this.panel) return;
      on(this.trigger, 'click', function () {
        var open = this.trigger.getAttribute('aria-expanded') === 'true';
        this.trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        this.panel.hidden = open;
      }.bind(this));
    }
  }
  define('solva-disclosure', SolvaDisclosure);
})();
